/**
 * Stori pricing data — single source of truth.
 * Edit prices, copy, and feature inclusion here.
 *
 * `included` values:
 *   true            → checkmark
 *   false           → empty / dash
 *   "string"        → exact text rendered (e.g. "180" for credits, "Phone" for partial)
 */

export const PLAN_KEYS = ["essential", "signature", "unlimited"];

export const PLAN_LABELS = {
  essential: "ESSENTIAL",
  signature: "SIGNATURE",
  unlimited: "UNLIMITED",
};

export const SOFTWARE_PLANS = [
  {
    key: "essential",
    name: "ESSENTIAL",
    tagline: "Everything you need to start",
    price: { weekly: 15, monthly: 50, annual: 40 },
    isSubscription: true,
    specs: {
      users: "—",
      publishLimit: "5,000",
      devices: "10",
      events: "20",
      support: "Chat, SMS, Email",
    },
    features: {
      socialPublishing: true,
      aiPhotoExperiences: false,
      playbooks: true,
      automation: false,
    },
    cta: { label: "Start for free", style: "secondary", action: "trial_signup" },
    popular: false,
  },
  {
    key: "signature",
    name: "SIGNATURE",
    tagline: "Advanced tools for businesses",
    price: { weekly: 30, monthly: 100, annual: 80 },
    isSubscription: true,
    specs: {
      users: "5",
      publishLimit: "Unlimited",
      devices: "Unlimited",
      events: "Unlimited",
      support: "Phone, SMS, Chat, Email",
    },
    features: {
      socialPublishing: true,
      aiPhotoExperiences: true,
      playbooks: true,
      automation: true,
    },
    cta: { label: "Start for free", style: "primary", action: "trial_signup" },
    popular: true,
  },
  {
    key: "unlimited",
    name: "UNLIMITED",
    tagline: "Premium experience with dedicated support",
    price: { weekly: 90, monthly: 300, annual: 240 },
    isSubscription: true,
    specs: {
      users: "4",
      publishLimit: "Unlimited",
      devices: "Unlimited",
      events: "Unlimited",
      support: "Phone, SMS, Chat, Email",
    },
    features: {
      socialPublishing: true,
      aiPhotoExperiences: true,
      playbooks: true,
      automation: true,
    },
    cta: { label: "Start for free", style: "primary", action: "trial_signup" },
    popular: false,
  },
];

// ---------------------------------------------------------------------------
// Comparison-table feature groups
// ---------------------------------------------------------------------------
export const FEATURE_GROUPS = [
  {
    name: "Core",
    features: [
      {
        name: "Custom digital & print layouts",
        description:
          "Text, graphics, QR codes, gradients, borders, and more — to elevate design from headers",
        included: { essential: true, signature: true, unlimited: true },
      },
      {
        name: "Motion & AI capture",
        description: "Still photos, image grids, GIFs, and boomerangs",
        included: { essential: true, signature: true, unlimited: true },
      },
      {
        name: "Convenient photo delivery",
        description: "QR code, text message, email, WhatsApp, AirDrop, AirPrint",
        included: { essential: true, signature: true, unlimited: true },
      },
      {
        name: "Online galleries",
        description: "Social-sharing privacy controls",
        included: { essential: true, signature: true, unlimited: true },
      },
      {
        name: "Online dashboard",
        description: "Manage settings remotely from any laptop or phone",
        included: { essential: true, signature: true, unlimited: true },
      },
      {
        name: "Library of themes & props",
        description: "100+ to get you started",
        included: { essential: true, signature: true, unlimited: true },
      },
      {
        name: "Filters, GIF effects & digital props",
        description: "Instagram-style filters, smoothing, and more",
        included: { essential: true, signature: true, unlimited: true },
      },
      {
        name: "7-day support",
        description: "Email or chat with support every day of the week",
        included: { essential: true, signature: true, unlimited: true },
      },
      {
        name: "Removes ads",
        description: "Hides the Stori Booth promo prompts",
        included: { essential: true, signature: true, unlimited: true },
      },
    ],
  },
  {
    name: "Capture",
    features: [
      {
        name: "Multiple layouts",
        description: "Guests can choose from one or more layouts with unique designs",
        included: { essential: true, signature: true, unlimited: true },
      },
      {
        name: "Connect DSLR or mirrorless camera",
        description: "DSLRs over USB or wireless connection",
        included: { essential: true, signature: true, unlimited: true },
      },
      {
        name: "Full-length video capture",
        description: "Up to 30 seconds with audio",
        included: { essential: true, signature: true, unlimited: true },
      },
      {
        name: "Background effects",
        description: "Replace backgrounds with green-screen, sci-fi, cinematic styles",
        included: { essential: true, signature: true, unlimited: true },
      },
      {
        name: "Save captures to Dropbox",
        description: "In-app syncing with a Dropbox account",
        included: { essential: true, signature: true, unlimited: true },
      },
      {
        name: "AI effects",
        description: "Apply AI effects from a library of pre-built styles",
        badges: ["CREDITS"],
        included: { essential: true, signature: true, unlimited: true },
      },
    ],
  },
  {
    name: "Virtual & AI",
    features: [
      {
        name: "Virtual Booth",
        description:
          "Deploy a custom virtual experience from anywhere via link or QR code",
        badges: ["NEW"],
        included: { essential: true, signature: true, unlimited: true },
      },
      {
        name: "AI-generated layout images",
        description: "Design printable layouts with AI in seconds",
        badges: ["AI"],
        included: { essential: true, signature: true, unlimited: true },
      },
    ],
  },
  {
    name: "Branding & Analytics",
    features: [
      {
        name: "Custom branding",
        description: "Set the logo screen, gallery backdrop, frame, and more",
        included: { essential: true, signature: true, unlimited: true },
      },
      {
        name: "Custom photo delivery messages",
        description: "Custom company name on outgoing emails, SMS, and WhatsApp",
        included: { essential: true, signature: true, unlimited: true },
      },
      {
        name: "Analytics & reporting",
        description: "Trending photos, views, impressions, demographics",
        included: { essential: true, signature: true, unlimited: true },
      },
      {
        name: "Custom digital props & stickers",
        description: "Design your own and tag specific products",
        included: { essential: true, signature: true, unlimited: true },
      },
    ],
  },
  {
    name: "Marketing",
    features: [
      {
        name: "Guest-editable text layers & add-ons",
        description: "Let guests personalize photos with editable text fields",
        included: { essential: false, signature: true, unlimited: true },
      },
      {
        name: "Custom AI effects",
        description:
          "Create and customize AI effects with built-in pose and identity controls",
        badges: ["CREDITS"],
        included: { essential: false, signature: true, unlimited: true },
      },
      {
        name: "Lead & data capture",
        description: "Name, email, phone, DOB, ZIP, custom text, checkboxes",
        included: { essential: false, signature: true, unlimited: true },
      },
      {
        name: "Advanced text messaging",
        description: "Full text message customization and international SMS",
        included: { essential: false, signature: true, unlimited: true },
      },
    ],
  },
  {
    name: "Customization & Integrations",
    features: [
      {
        name: "Full email customization",
        description: "Sender name, address, and HTML email body",
        included: { essential: false, signature: true, unlimited: true },
      },
      {
        name: "API integration",
        description: "Import collected data into your CRM",
        included: { essential: false, signature: true, unlimited: true },
      },
      {
        name: "Drawing edit tool",
        description: "Let guests draw on their photo captures",
        included: { essential: false, signature: true, unlimited: true },
      },
      {
        name: "Phone support",
        description: "Get additional support over the phone",
        included: { essential: false, signature: true, unlimited: true },
      },
    ],
  },
  {
    name: "AI Insights & Compliance",
    features: [
      {
        name: "AI effect guest reports",
        description: "Per-face insights to personalize AI effects",
        badges: ["CREDITS"],
        included: { essential: false, signature: false, unlimited: true },
      },
      {
        name: "In-app system legal terms",
        description: "Privacy policy, terms of service",
        included: { essential: true, signature: true, unlimited: true },
      },
      {
        name: "Moderation",
        description: "Approve photos before they are sent or appear online",
        included: { essential: true, signature: true, unlimited: true },
      },
      {
        name: "Age gate",
        description: "Confirm guest age before booth use",
        included: { essential: true, signature: true, unlimited: true },
      },
      {
        name: "Data compliance tools",
        description: "Tools to manage data information as required",
        included: { essential: false, signature: true, unlimited: true },
      },
      {
        name: "Security questionnaires & IT assessments",
        description: "Work with our team to complete enterprise security reviews",
        included: { essential: false, signature: false, unlimited: true },
      },
      {
        name: "AI effect bonus credits",
        description: "Monthly bonus AI generation credits",
        included: { essential: "120", signature: "180", unlimited: "240" },
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Event-rental packages (placeholders — copy + prices to be finalized)
// ---------------------------------------------------------------------------
export const EVENT_AUDIENCES = ["private", "brands"];
export const EVENT_AUDIENCE_LABELS = {
  private: "Private",
  brands: "Brands",
};

export const EVENT_PACKAGES = [
  {
    key: "core",
    name: "CORE",
    tagline: "Perfect for intimate gatherings & launches",
    prices: { private: "$500", brands: "$1,500" },
    duration: "Up to 4 hours",
    includes: [
      "On-site Stori activation (1 station)",
      "Branded digital templates",
      "Live photo & GIF delivery via QR",
      "Real-time online gallery",
      "On-site attendant",
    ],
    cta: { label: "Request Date", style: "secondary", action: "demo_request" },
    popular: false,
  },
  {
    key: "vip",
    name: "VIP",
    tagline: "Our most-requested event experience",
    prices: { private: "$1,000", brands: "$3,000" },
    duration: "Up to 6 hours",
    includes: [
      "On-site Stori activation (2 stations)",
      "Custom branded layouts & motion templates",
      "AI-powered photo experiences",
      "Lead & data capture with CRM export",
      "Real-time online gallery + analytics",
      "Dedicated on-site attendant",
    ],
    cta: { label: "Request Date", style: "primary", action: "demo_request" },
    popular: true,
  },
  {
    key: "yolo",
    name: "YOLO",
    tagline: "White-glove experience for premium activations",
    prices: { private: "$2,000", brands: "$6,000" },
    duration: "Full day · multi-day available",
    includes: [
      "Multi-station Stori deployment",
      "Bespoke creative direction & set design",
      "Custom AI effects with brand identity controls",
      "VIP & influencer detection playbooks",
      "Lead capture + Salesforce / HubSpot / Klaviyo sync",
      "Post-event analytics & ROI report",
      "Dedicated event producer + on-site team",
    ],
    cta: { label: "Request Date", style: "primary", action: "demo_request" },
    popular: false,
  },
];

// ---------------------------------------------------------------------------
// AI credits
// ---------------------------------------------------------------------------
export const AI_CREDITS = {
  pricePerCredit: "$0.50",
  bulkTiers: [
    { range: "100 – 499 credits", price: "$0.50 / credit" },
    { range: "500 – 1,999 credits", price: "$0.45 / credit" },
    { range: "2,000 – 4,999 credits", price: "$0.40 / credit" },
    { range: "5,000+ credits", price: "Contact sales" },
  ],
  modelCosts: [
    { model: "Stori Style Transfer", credits: "1" },
    { model: "AI Background Replace", credits: "2" },
    { model: "Custom AI Effect (per face)", credits: "3" },
    { model: "Premium Cinematic Render", credits: "5" },
  ],
};

// ---------------------------------------------------------------------------
// FAQ
// ---------------------------------------------------------------------------
export const FAQ = [
  {
    q: "Can I switch plans anytime?",
    a: "Yes. You can upgrade, downgrade, or cancel from your dashboard at any time. Plan changes take effect on the next billing cycle, with prorated credits applied where applicable.",
  },
  {
    q: "What are AI Credits and how do they work?",
    a: "AI Credits power generative photo experiences — style transfer, background replacement, and custom effects. Each subscription tier includes a monthly bonus credit allowance, and you can purchase additional credits in bulk at $0.50 each (volume discounts apply).",
  },
  {
    q: "Do you offer custom enterprise installations?",
    a: "Yes. Stori installs permanent activations in luxury hotels, flagship retail, fine dining, and corporate spaces. Reach out and our team will scope hardware, integrations, and a tailored playbook for your space.",
  },
  {
    q: "What's included in event rental setup?",
    a: "Every Event Rental package includes hardware delivery, on-site setup and breakdown, a trained attendant, custom-branded templates, and a real-time online gallery. Higher tiers add AI photo experiences, lead capture, and post-event analytics.",
  },
  {
    q: "Is there a free trial?",
    a: "Yes — every paid plan includes a 7-day free trial with full feature access. No credit card required to start, and you can cancel anytime before the trial ends.",
  },
];
